/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { hexToRgb } from "@Actions/estimation";
import { setTaskCost, setTaskActualCost, setTaskEarnedValue } from "@Actions/task";
import { CubeGlobal, HoopsViewerContext, IsolateZoomHelper } from "@Features/viewer-core";
import { getFileVersionIdFromFileId, getNodeIdsFromCubeUniqueId } from "@Services/cubeViewHelper";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import { dispatchModelLoadingToast } from "@Actions/Viewer";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { PROJECT } from "@Helpers/constants";
import { Task as GanttTask, GanttStatic } from "./dhtmlxgantt";
import { GANTT_VIEW_TYPE, SimulationProps } from "../interface";
import type { AppearanceData, TaskTypeData } from "@WebTypes/scheduleDomain";
import useGanttHelper from "./useGanttHelper";
import { Color } from "@ts3d-hoops/web-viewer";
import { ackSimulationTime } from "./simulationTimeAck";

declare const gantt: GanttStatic;
interface Task extends GanttTask {
  cost?: number;
  actualCost?: number;
  plannedStartDate?: Date | null;
  plannedEndDate?: Date | null;
  actualStartDate?: Date | null;
  actualEndDate?: Date | null;
  _plannedStartTs?: number;
  _plannedEndTs?: number;
  _actualStartTs?: number;
  _actualEndTs?: number;
  taskTypeData?: TaskTypeData;
}

const NO_SCHEDULE_TIME = 1;

const Simulation = (props: SimulationProps) => {
  const { isModelStructureReady, viewerImpl, isSettingsApplied }: any = useContext(HoopsViewerContext);
  const { activeTab, ganttDataAvailable, viewType, setDisableTabs } = props;
  const dispatch = useDispatch();
  const isolateZoomHelper = useRef<any>(null);
  const prevTotalCostRef = useRef<number>(0);
  const prevTotalActualCostRef = useRef<number>(0);
  const prevTotalEarnedValueRef = useRef<number>(0);
  const [allTasks] = useState<Task[]>(() => gantt.getTaskByTime());
  // Pre-sorted arrays (sort once, reuse on every slider tick)
  const sortedByEndDateAsc = useRef<Task[]>([]);
  const sortedByStartDateDesc = useRef<Task[]>([]);
  const timestampsCached = useRef(false);
  const sliderTimer = useSelector((state: RootState) => state.sliderTimer);
  const GanttDetails = useSelector((state: any) => state.GanttDetails);
  const selectionSetTaskElements = useSelector((state: RootState) => state.selectionSetTaskElements);
  const nodeIdCache = useRef(new Map<string, number[]>());
  // Cache nodeIds per task id — elements don't change during simulation
  const taskNodeIdsCache = useRef(new Map<string, number[]>());
  // Cache Color objects per hex — avoid re-parsing hex every tick
  const colorCache = useRef(new Map<string, Color>());
  // Track previous appearance state to skip redundant HOOPS API calls
  const prevNodeAppearanceRef = useRef(new Map<number, string>());
  const isFirstTick = useRef(true);
  // Track previous running task ids to skip gantt.clearAll/parse when unchanged (null = first tick)
  const prevRunningTaskIds = useRef<Set<string | number> | null>(null);
  const { HOUR } = useGanttHelper();
  const isPercentageBased = viewType === GANTT_VIEW_TYPE.PLANNED_AGAINST_ACTUAL_PERCENTAGE;
  const isPlannedAgainstActual = viewType === GANTT_VIEW_TYPE.PLANNED_AGAINST_ACTUAL || isPercentageBased;

  const getNodeIdsForElement = (id: string): number[] => {
    let ids = nodeIdCache.current.get(id);
    if (!ids) {
      const fileId = id.split("_")[0];
      const fileVersionId = getFileVersionIdFromFileId(fileId);
      if (!fileVersionId) return [];
      ids = (getNodeIdsFromCubeUniqueId(id, undefined, fileVersionId) || []).filter((n) => !isNaN(n));
      if (ids.length) nodeIdCache.current.set(id, ids);
    }
    return ids;
  };

  const getUniqueNodeIds = (taskId: string, elementIdList: string[]): number[] => {
    let cached = taskNodeIdsCache.current.get(taskId);
    if (cached) return cached;
    const nodeIdSet = new Set<number>();
    for (const id of elementIdList) {
      for (const nodeId of getNodeIdsForElement(id)) {
        nodeIdSet.add(nodeId);
      }
    }
    cached = Array.from(nodeIdSet);
    if (cached.length) taskNodeIdsCache.current.set(taskId, cached);
    return cached;
  };

  const getOrCreateColor = (hex: string): Color => {
    let color = colorCache.current.get(hex);
    if (!color) {
      const rgb = hexToRgb(hex);
      color = new Color(rgb.red, rgb.green, rgb.blue);
      colorCache.current.set(hex, color);
    }
    return color;
  };

  const applyAppearanceGroups = (
    appearanceGroups: Map<string, { nodeIds: number[]; appearanceName: string; appearanceColour: string | null }>,
  ) => {
    if (!getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })) {
      dispatch(dispatchModelLoadingToast());
      return;
    }

    // Build current appearance key per node
    const currentNodeAppearance = new Map<number, string>();
    for (const { nodeIds, appearanceName, appearanceColour } of appearanceGroups.values()) {
      if (!nodeIds?.length) continue;
      const key = `${appearanceName}|${appearanceColour ?? ""}`;
      for (const id of nodeIds) {
        currentNodeAppearance.set(id, key);
      }
    }

    // Diff against previous state — only process changed nodes
    const prev = prevNodeAppearanceRef.current;
    const hideNodeIds: number[] = [];
    const showNodeIds: number[] = [];
    const unsetColorNodeIds: number[] = [];
    const colorGroups = new Map<string, number[]>();

    for (const { nodeIds, appearanceName, appearanceColour } of appearanceGroups.values()) {
      if (!nodeIds?.length) continue;
      const key = `${appearanceName}|${appearanceColour ?? ""}`;
      for (const id of nodeIds) {
        // Skip if this node had the same appearance last tick
        if (prev.get(id) === key) continue;
        if (appearanceName === "Hide") {
          hideNodeIds.push(id);
        } else if (appearanceName === "None" || appearanceName === "Model Appearance") {
          showNodeIds.push(id);
          unsetColorNodeIds.push(id);
        } else if (appearanceColour) {
          showNodeIds.push(id);
          let group = colorGroups.get(appearanceColour);
          if (!group) {
            group = [];
            colorGroups.set(appearanceColour, group);
          }
          group.push(id);
        }
      }
    }

    // Handle nodes that were in previous state but not in current — hide them
    for (const [nodeId] of prev) {
      if (!currentNodeAppearance.has(nodeId)) {
        hideNodeIds.push(nodeId);
        unsetColorNodeIds.push(nodeId);
      }
    }

    if (hideNodeIds.length) {
      viewerImpl.hwv.model.setNodesVisibility(hideNodeIds, false, null);
    }
    if (showNodeIds.length) {
      viewerImpl.hwv.model.setNodesVisibility(showNodeIds, true, null);
    }
    if (unsetColorNodeIds.length) {
      viewerImpl.hwv.model.resetNodesMaterial(unsetColorNodeIds, ["faceColor", "lineColor", "pointColor"]);
    }
    for (const [hex, nodeIds] of colorGroups) {
      const color = getOrCreateColor(hex);
      viewerImpl.hwv.model.setNodesMaterial(nodeIds, { faceColor: color, lineColor: color, pointColor: color });
    }

    // Store current as previous for next tick
    prevNodeAppearanceRef.current = currentNodeAppearance;
  };

  const isAllIfcSpace = (nodeIds: any) =>
    nodeIds.every((nodeId: any) => viewerImpl.hwv.model.hasEffectiveGenericType(nodeId, "IfcSpace"));

  const showOnlySelectedNodeIds = async (nodeIds: any) => {
    await isolateZoomHelper.current?.isolateNodes(nodeIds, isAllIfcSpace(nodeIds) ? false : null);
  };

  useEffect(() => {
    if (getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })) {
      isolateZoomHelper.current = new IsolateZoomHelper(viewerImpl.hwv);
      if (activeTab === "Simulation" && ganttDataAvailable) {
        dispatch(setTaskCost(0));
        dispatch(setTaskActualCost(0));
        dispatch(setTaskEarnedValue(0));
        const nodeIdSet = new Set<number>();
        for (const elementIds of Object.values(selectionSetTaskElements) as string[][]) {
          for (const id of elementIds) {
            for (const nodeId of getNodeIdsForElement(id)) {
              nodeIdSet.add(nodeId);
            }
          }
        }
        if (nodeIdSet.size) {
          showOnlySelectedNodeIds(Array.from(nodeIdSet));
        }
      }
      setDisableTabs(false);
    } else {
      setDisableTabs(true);
      dispatch(dispatchModelLoadingToast());
    }
  }, [activeTab, isModelStructureReady, ganttDataAvailable, isSettingsApplied, viewerImpl, hwv]);

  useEffect(() => {
    // Reset caches when viewType changes since dates swap between planned/actual
    timestampsCached.current = false;
    taskNodeIdsCache.current.clear();
    prevNodeAppearanceRef.current.clear();
    isFirstTick.current = true;
    prevRunningTaskIds.current = null;
  }, [viewType, ganttDataAvailable]);

  const dispatchTotalCost = (runningTasks: Task[], completedTasks: Task[], currentDateTime: number) => {
    const { costData, actualCostData, earnedValueData } = calculateTasksCost(
      runningTasks,
      completedTasks,
      currentDateTime,
    );

    const sumValues = (data: Record<string | number, number>) =>
      Object.values(data).reduce((total, value) => total + (value || 0), 0);
    const totalCost = sumValues(costData);
    const totalActualCost = sumValues(actualCostData);
    const totalEarnedValue = sumValues(earnedValueData);

    // Only dispatch if the value actually changed — avoids unnecessary Redux updates
    if (totalCost !== prevTotalCostRef.current) {
      prevTotalCostRef.current = totalCost;
      dispatch(setTaskCost(totalCost));
    }
    if (totalActualCost !== prevTotalActualCostRef.current) {
      prevTotalActualCostRef.current = totalActualCost;
      dispatch(setTaskActualCost(totalActualCost));
    }
    if (totalEarnedValue !== prevTotalEarnedValueRef.current) {
      prevTotalEarnedValueRef.current = totalEarnedValue;
      dispatch(setTaskEarnedValue(totalEarnedValue));
    }
  };

  const cacheTaskTimestamps = (tasks: Task[]) => {
    if (timestampsCached.current) return;
    timestampsCached.current = true;
    for (const task of tasks) {
      task._plannedStartTs = task.plannedStartDate ? new Date(task.plannedStartDate).getTime() : undefined;
      task._plannedEndTs = task.plannedEndDate ? new Date(task.plannedEndDate).getTime() : undefined;
      task._actualStartTs = task.actualStartDate ? new Date(task.actualStartDate).getTime() : undefined;
      task._actualEndTs = task.actualEndDate ? new Date(task.actualEndDate).getTime() : undefined;
    }
    // Sort once after caching timestamps, reused on every slider tick
    sortedByEndDateAsc.current = [...tasks].sort((a, b) => (getHighestEndDate(a) ?? 0) - (getHighestEndDate(b) ?? 0));
    sortedByStartDateDesc.current = [...tasks].sort(
      (a, b) => (getLowestStartDate(b) ?? 0) - (getLowestStartDate(a) ?? 0),
    );
  };

  const categorizeTaskPlanned = (
    task: Task,
    currentDateTime: number,
    allTasksYetToComplete: Task[],
    completedTasks: Task[],
    runningTasks: Task[],
  ) => {
    const start = task._plannedStartTs;
    const end = task._plannedEndTs;
    if (end && currentDateTime > end) {
      completedTasks.push(task);
    } else if (start) {
      allTasksYetToComplete.push(task);
      if (currentDateTime >= start) {
        runningTasks.push(task);
      }
    }
  };

  const categorizeTaskActual = (
    task: Task,
    currentDateTime: number,
    allTasksYetToComplete: Task[],
    completedTasks: Task[],
    runningTasks: Task[],
  ) => {
    const actualStart = task._actualStartTs;
    const actualEnd = task._actualEndTs ?? 0;
    if (actualStart) {
      if (currentDateTime > actualEnd) {
        completedTasks.push(task);
      } else {
        allTasksYetToComplete.push(task);
        if (currentDateTime >= actualStart) {
          runningTasks.push(task);
        }
      }
    }
  };

  const categorizeTaskPlannedAgainstActual = (
    task: Task,
    currentDateTime: number,
    allTasksYetToComplete: Task[],
    completedTasks: Task[],
    runningTasks: Task[],
  ) => {
    const actualStart = task._actualStartTs ?? 0;
    const actualEnd = task._actualEndTs ?? 0;
    const plannedStart = task._plannedStartTs ?? 0;
    const plannedEnd = task._plannedEndTs ?? 0;
    const minStart = Math.min(actualStart, plannedStart);
    const maxEnd = Math.max(actualEnd, plannedEnd);
    if (plannedEnd && actualStart) {
      if (currentDateTime > maxEnd) {
        completedTasks.push(task);
      } else {
        allTasksYetToComplete.push(task);
        if (currentDateTime >= minStart && currentDateTime <= maxEnd) {
          runningTasks.push(task);
        }
      }
    }
  };

  const categorizeTasks = (currentDateTime: number) => {
    let allTasksYetToComplete: Task[] = [];
    let completedTasks: Task[] = [];
    let runningTasks: Task[] = [];

    if (viewType === GANTT_VIEW_TYPE.PLANNED) {
      for (const task of allTasks) {
        categorizeTaskPlanned(task, currentDateTime, allTasksYetToComplete, completedTasks, runningTasks);
      }
    } else if (viewType === GANTT_VIEW_TYPE.ACTUAL) {
      for (const task of allTasks) {
        categorizeTaskActual(task, currentDateTime, allTasksYetToComplete, completedTasks, runningTasks);
      }
    } else if (isPlannedAgainstActual) {
      for (const task of allTasks) {
        categorizeTaskPlannedAgainstActual(task, currentDateTime, allTasksYetToComplete, completedTasks, runningTasks);
      }
    }

    return { allTasksYetToComplete, completedTasks, runningTasks };
  };

  const getHighestEndDate = (eachTask: Task): number | undefined => {
    if (viewType === GANTT_VIEW_TYPE.PLANNED) {
      return eachTask._plannedEndTs;
    } else if (viewType === GANTT_VIEW_TYPE.ACTUAL) {
      return eachTask._actualEndTs;
    } else if (isPlannedAgainstActual) {
      return Math.max(eachTask._actualEndTs ?? 0, eachTask._plannedEndTs ?? 0);
    }
  };

  const getLowestStartDate = (eachTask: Task): number | undefined => {
    if (viewType === GANTT_VIEW_TYPE.PLANNED) {
      return eachTask._plannedStartTs;
    } else if (viewType === GANTT_VIEW_TYPE.ACTUAL) {
      return eachTask._actualStartTs;
    } else if (isPlannedAgainstActual) {
      return Math.min(eachTask._actualStartTs ?? 0, eachTask._plannedStartTs ?? 0);
    }
  };

  const getPlannedAgainstActualAppearance = (eachTask: Task, currentDateTime: number): AppearanceData | null => {
    const plannedStartTs = eachTask._plannedStartTs ?? 0;
    const plannedEndTs = eachTask._plannedEndTs ?? 0;
    const actualStartTs = eachTask._actualStartTs ?? 0;
    const actualEndTs = eachTask._actualEndTs ?? plannedEndTs;

    const { taskTypeData } = eachTask;
    if (!taskTypeData) return null;

    const laterStartTs = Math.max(plannedStartTs, actualStartTs);
    const earlierEndTs = Math.min(plannedEndTs, actualEndTs);
    const laterEndTs = Math.max(plannedEndTs, actualEndTs);

    const startDeviationAppearance =
      plannedStartTs < actualStartTs ? taskTypeData.lateAppearanceData : taskTypeData.earlyAppearanceData;

    const endDeviationAppearance =
      plannedEndTs < actualEndTs ? taskTypeData.lateAppearanceData : taskTypeData.earlyAppearanceData;

    if (currentDateTime < laterStartTs) {
      return startDeviationAppearance;
    }
    if (currentDateTime < earlierEndTs) {
      return taskTypeData.startAppearanceData;
    }
    if (currentDateTime < laterEndTs) {
      return endDeviationAppearance;
    }
    return taskTypeData.endAppearanceData;
  };

  const getPercentageBasedAppearance = (eachTask: Task, currentDateTime: number): AppearanceData | null => {
    const plannedStartTs = eachTask._plannedStartTs ?? 0;
    const plannedEndTs = eachTask._plannedEndTs ?? 0;
    const actualStartTs = eachTask._actualStartTs ?? 0;
    const actualEndTs = eachTask._actualEndTs ?? plannedEndTs;

    const { taskTypeData } = eachTask;
    if (!taskTypeData) return null;

    if (actualEndTs && currentDateTime >= actualEndTs) {
      return taskTypeData.endAppearanceData;
    }

    const plannedDuration = plannedEndTs - plannedStartTs;
    const actualDuration = actualEndTs - actualStartTs;

    const plannedFraction =
      plannedDuration > 0
        ? Math.min(1, Math.max(0, (currentDateTime - plannedStartTs) / plannedDuration))
        : currentDateTime >= plannedStartTs
          ? 1
          : 0;

    const actualFraction =
      actualStartTs && actualDuration > 0
        ? Math.min(1, Math.max(0, (currentDateTime - actualStartTs) / actualDuration))
        : 0;

    if (actualFraction === plannedFraction) {
      return taskTypeData.startAppearanceData;
    }
    if (actualFraction > plannedFraction) {
      return taskTypeData.earlyAppearanceData;
    }
    if (actualFraction < plannedFraction) {
      return taskTypeData.lateAppearanceData;
    }
    return null;
  };

  const getTaskAppearance = (eachTask: Task, currentDateTime: number): AppearanceData | null => {
    if (currentDateTime === NO_SCHEDULE_TIME) return null;
    const { taskTypeData } = eachTask;
    if (!taskTypeData) return null;
    const lowestStartDate = getLowestStartDate(eachTask);
    if (lowestStartDate !== undefined && currentDateTime >= lowestStartDate) {
      if (viewType === GANTT_VIEW_TYPE.PLANNED || viewType === GANTT_VIEW_TYPE.ACTUAL) {
        return taskTypeData.startAppearanceData;
      } else if (isPercentageBased) {
        return getPercentageBasedAppearance(eachTask, currentDateTime);
      } else if (viewType === GANTT_VIEW_TYPE.PLANNED_AGAINST_ACTUAL) {
        return getPlannedAgainstActualAppearance(eachTask, currentDateTime);
      }
    } else {
      return taskTypeData.simulationStartAppearanceData;
    }
    return null;
  };

  // Priority: Start Appearance = 3 (highest), End Appearance = 2, Simulation Start = 1 (lowest)
  const APPEARANCE_PRIORITY = {
    START: 3,
    END: 2,
    SIMULATION_START: 1,
  } as const;

  const buildNodeAppearanceMap = (
    completedTasks: Task[],
    allTasksYetToComplete: Task[],
    currentDateTime: number,
  ): {
    nodeAppearanceMap: Map<number, { appearanceName: string; appearanceColour: string | null }>;
    completedElementIds: Set<string>;
  } => {
    const nodePriorityMap = new Map<number, number>();
    const nodeAppearanceMap = new Map<number, { appearanceName: string; appearanceColour: string | null }>();
    const completedElementIds = new Set<string>();

    const assignIfHigherPriority = (nodeIds: number[], appearanceData: AppearanceData | null, priority: number) => {
      if (!appearanceData) return;
      for (const nodeId of nodeIds) {
        if ((nodePriorityMap.get(nodeId) ?? 0) <= priority) {
          nodePriorityMap.set(nodeId, priority);
          nodeAppearanceMap.set(nodeId, {
            appearanceName: appearanceData.name,
            appearanceColour: appearanceData.colour ?? null,
          });
        }
      }
    };

    // End appearance — priority 2
    // Use pre-sorted array, filter to completed tasks via O(1) Set lookup
    const completedSet = new Set(completedTasks);
    const sortedCompleted = sortedByEndDateAsc.current.filter((t) => completedSet.has(t));
    for (const eachTask of sortedCompleted) {
      const elementIdList = selectionSetTaskElements[eachTask.id];
      if (!elementIdList?.length) continue;
      for (const id of elementIdList) completedElementIds.add(id);
      if (eachTask.taskTypeData?.name) {
        const highestEndDate = getHighestEndDate(eachTask);
        if (highestEndDate !== undefined && currentDateTime >= highestEndDate) {
          const nodeIds = getUniqueNodeIds(String(eachTask.id), elementIdList);
          if (nodeIds.length) {
            assignIfHigherPriority(nodeIds, eachTask.taskTypeData.endAppearanceData, APPEARANCE_PRIORITY.END);
          }
        }
      }
    }

    // Start appearance (priority 3, highest) and Simulation Start (priority 1, lowest)
    // Use pre-sorted array, filter to yet-to-complete tasks via O(1) Set lookup
    const yetToCompleteSet = new Set(allTasksYetToComplete);
    const sortedYetToComplete = sortedByStartDateDesc.current.filter((t) => yetToCompleteSet.has(t));
    for (const eachTask of sortedYetToComplete) {
      if (!eachTask.taskTypeData?.name) continue;
      const elementIdList = selectionSetTaskElements[eachTask.id];
      if (!elementIdList?.length) continue;
      const nodeIds = getUniqueNodeIds(String(eachTask.id), elementIdList);
      if (!nodeIds.length) continue;
      const appearance = getTaskAppearance(eachTask, currentDateTime);
      if (appearance) {
        const isSimulationStart = appearance === eachTask.taskTypeData.simulationStartAppearanceData;
        const priority = isSimulationStart ? APPEARANCE_PRIORITY.SIMULATION_START : APPEARANCE_PRIORITY.START;
        assignIfHigherPriority(nodeIds, appearance, priority);
      }
    }

    return { nodeAppearanceMap, completedElementIds };
  };

  const removeSelectionForYetToComplete = (allTasksYetToComplete: Task[], completedElementIds: Set<string>) => {
    const selectedSelectionItems = viewerImpl.hwv.selectionManager.getResults();
    const selectionItemMap = new Map<number, any>();
    for (const item of selectedSelectionItems) {
      selectionItemMap.set(item.getNodeId(), item);
    }

    for (const eachTask of allTasksYetToComplete) {
      const elementIdList = selectionSetTaskElements[eachTask.id];
      if (!elementIdList?.length) continue;
      for (const elementId of elementIdList) {
        if (completedElementIds.has(elementId)) continue;
        const nodeIds = getNodeIdsForElement(elementId);
        for (const nodeId of nodeIds) {
          const selectionItem = selectionItemMap.get(nodeId);
          if (selectionItem) {
            viewerImpl.hwv.selectionManager.remove(selectionItem);
            selectionItemMap.delete(nodeId);
          }
        }
      }
    }
  };

  const calculateTasksCost = (runningTasks: Task[], completedTasks: Task[], currentDateTime: number) => {
    const costData: Record<string | number, number> = {};
    const actualCostData: Record<string | number, number> = {};
    const earnedValueData: Record<string | number, number> = {};

    for (const task of runningTasks) {
      if (task.type !== PROJECT) {
        if (task.plannedStartDate && task.plannedEndDate) {
          const startTs: number = task._plannedStartTs ?? 0;
          let totalHours = gantt.calculateDuration({
            start_date: task.plannedStartDate,
            end_date: task.plannedEndDate,
            task: task,
          });
          let totalHoursElapsed = gantt.calculateDuration({
            start_date: new Date(startTs),
            end_date: new Date(currentDateTime),
            task: task,
          });

          if (GanttDetails?.config?.duration_unit !== HOUR) {
            totalHours = totalHours * 24;
            totalHoursElapsed = totalHoursElapsed * 24;
          }
          if (totalHoursElapsed < 0) {
            totalHoursElapsed = 0;
          }
          const plannedCost = task.cost ?? 0;
          const costPerHour = plannedCost / (totalHours || 1);
          const elapsedCost = costPerHour * totalHoursElapsed;
          costData[task.id] = elapsedCost > plannedCost ? plannedCost : elapsedCost;
        } else if (task.plannedStartDate && !task.plannedEndDate) {
          costData[task.id] = task.cost ?? 0;
        }
        if (task.actualStartDate) {
          actualCostData[task.id] = task.actualCost ?? 0;
        }
        earnedValueData[task.id] = (task.cost ?? 0) * (task.progress ?? 0);
      }
    }
    for (const task of completedTasks) {
      if (task.type !== PROJECT) {
        costData[task.id] = Number(task.cost) || 0;
        actualCostData[task.id] = Number(task.actualCost) || 0;
        earnedValueData[task.id] = (Number(task.cost) || 0) * (Number(task.progress) || 0);
      }
    }
    return { costData, actualCostData, earnedValueData };
  };

  const assignInitialColours = (
    allTasksYetToComplete: Task[],
    completedTasks: Task[],
    runningTasks: Task[],
    currentDateTime: number,
  ) => {
    const { nodeAppearanceMap, completedElementIds } = buildNodeAppearanceMap(
      completedTasks,
      allTasksYetToComplete,
      currentDateTime,
    );

    // Convert per-node map to appearance groups
    const appearanceGroups = new Map<
      string,
      { nodeIds: number[]; appearanceName: string; appearanceColour: string | null }
    >();
    for (const [nodeId, { appearanceName, appearanceColour }] of nodeAppearanceMap) {
      const key = `${appearanceName}|${appearanceColour ?? ""}`;
      if (!appearanceGroups.has(key)) {
        appearanceGroups.set(key, { nodeIds: [], appearanceName, appearanceColour });
      }
      appearanceGroups.get(key)!.nodeIds.push(nodeId);
    }

    applyAppearanceGroups(appearanceGroups);

    if (allTasksYetToComplete.length) {
      removeSelectionForYetToComplete(allTasksYetToComplete, completedElementIds);
    }

    dispatchTotalCost(runningTasks, completedTasks, currentDateTime);
    setDisableTabs(false);
  };

  const run = () => {
    setDisableTabs(true);
    const currentDateTime: number = sliderTimer?.scheduleTime ? Number(sliderTimer.scheduleTime) : NO_SCHEDULE_TIME;

    cacheTaskTimestamps(allTasks);

    const { allTasksYetToComplete, completedTasks, runningTasks } = categorizeTasks(currentDateTime);

    // Only hide entire model on first tick; delta updates handle subsequent ticks
    if (isFirstTick.current) {
      isFirstTick.current = false;
      const absoluteRoot = viewerImpl.hwv.model.getAbsoluteRootNode();
      viewerImpl.hwv.model.setNodesVisibility([absoluteRoot], false, null);
    }

    // Only rebuild gantt chart if running tasks changed (always clear on first tick)
    const currentRunningIds = new Set(runningTasks.map((t) => t.id));
    const prev = prevRunningTaskIds.current;
    let runningTasksChanged = prev === null || currentRunningIds.size !== prev.size;
    if (!runningTasksChanged) {
      for (const id of currentRunningIds) {
        if (!prev!.has(id)) {
          runningTasksChanged = true;
          break;
        }
      }
    }
    if (runningTasksChanged) {
      prevRunningTaskIds.current = currentRunningIds;
      gantt.clearAll();
      const finalData: Task[] = [];
      for (const task of runningTasks) {
        if (task.taskTypeId) {
          task.parent = 0;
          finalData.push(task);
        }
      }
      gantt.parse({ data: finalData });
    }

    assignInitialColours(allTasksYetToComplete, completedTasks, runningTasks, currentDateTime);
  };

  useEffect(() => {
    if (
      activeTab === "Simulation" &&
      ganttDataAvailable &&
      getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })
    ) {
      run();
    }
    ackSimulationTime(sliderTimer?.scheduleTime ?? NO_SCHEDULE_TIME);
  }, [sliderTimer, activeTab, viewType, isModelStructureReady, isSettingsApplied, viewerImpl]);
  return null;
};
export default Simulation;
